import { useEffect, useMemo, useState } from 'react';

import { ApolloQueryResult } from '@apollo/react-hooks';
import { TickMath } from '@uniswap/v3-sdk';
import { type WriteContractReturnType } from '@wagmi/core';
import axios, { AxiosResponse } from 'axios';
import Big from 'big.js';
import { ethers } from 'ethers';
import JSBI from 'jsbi';
import { borrowerNftAbi } from 'shared/lib/abis/BorrowerNft';
import { FilledGradientButton } from 'shared/lib/components/common/Buttons';
import { Text, Display } from 'shared/lib/components/common/Typography';
import {
  STACKING_SALMON_BOOST_MANAGER_ADDRESS,
  STACKING_SALMON_BORROWER_NFT_ADDRESS,
  UNISWAP_NONFUNGIBLE_POSITION_MANAGER_ADDRESS,
} from 'shared/lib/data/constants/ChainSpecific';
import { API_PRICE_RELAY_LATEST_URL, TERMS_OF_SERVICE_URL } from 'shared/lib/data/constants/Values';
import { GN, GNFormat } from 'shared/lib/data/GoodNumber';
import { Token } from 'shared/lib/data/Token';
import { getTokenBySymbol } from 'shared/lib/data/TokenData';
import {
  getValueOfLiquidity,
  UniswapPosition,
  UniswapV3GraphQL24HourPoolDataQueryResponse,
} from 'shared/lib/data/Uniswap';
import { useBorrowerNftRefs } from 'shared/lib/hooks/UseBorrowerNft';
import useChain from 'shared/lib/hooks/UseChain';
import { useLendingPair, useLendingPairs } from 'shared/lib/hooks/UseLendingPairs';
import { PriceRelayLatestResponse } from 'shared/lib/hooks/UsePriceRelay';
import { getTheGraphClient, Uniswap24HourPoolDataQuery } from 'shared/lib/util/GraphQL';
import { formatUSD } from 'shared/lib/util/Numbers';
import { generateBytes12Salt } from 'shared/lib/util/Salt';
import styled from 'styled-components';
import { encodeFunctionData, erc721Abi, Hex } from 'viem';
import {
  useAccount,
  useBalance,
  useReadContract,
  useSimulateContract,
  useWaitForTransactionReceipt,
  useWriteContract,
} from 'wagmi';

import SlippageWidget from './SlippageWidget';
import { BoostCardInfo } from '../../data/Uniboost';
import { BOOST_MAX, BOOST_MIN } from '../../pages/boost/ImportBoostPage';

const SECONDARY_COLOR = '#EC5D2A';
const TERTIARY_COLOR = '#FFFFFF';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding: 16px;
  width: 100%;
  border-radius: 8px;
  max-width: 500px;
  text-align: center;
`;

const SliderContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 300px;
  text-align: center;
  margin: 0 auto;
`;

const StyledDatalist = styled.datalist`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-top: 8px;
  width: 223px;
  margin-left: auto;
  margin-right: auto;

  option {
    color: #ffffff;
    position: relative;
    width: 25px;
  }

  option::before {
    content: '';
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    top: -8px;
    width: 2px;
    height: 4px;
    background-color: #ffffff;
  }
`;

const LeverageSlider = styled.input`
  appearance: none;
  -webkit-appearance: none;
  height: 7px;
  background: #ffffff;
  border-radius: 0px;
  background-repeat: no-repeat;
  width: 200px;
  margin: 0 auto;
  cursor: pointer;

  &::-webkit-slider-thumb {
    position: relative;
    -webkit-appearance: none;
    margin-top: -16px;
    height: 16px;
    width: 4px;
    background: #4197ff;
    z-index: 1;
    cursor: pointer;
  }

  &::-webkit-slider-runnable-track {
    appearance: none;
    -webkit-appearance: none;
    width: 100%;
    box-shadow: none;
    border: none;
    background: transparent;
  }
`;

type TwentyFourHourPoolData = {
  liquidity: Big;
  feesUSD: number;
};

type TokenQuote = {
  token: Token;
  price: number;
};

enum ImportState {
  READY_TO_APPROVE,
  ASKING_USER_TO_APPROVE,
  APPROVING,
  READY_TO_MINT,
  ASKING_USER_TO_MINT,
  UNABLE_TO_MINT,
  LOADING,
}

function getImportButtonState(state?: ImportState) {
  switch (state) {
    case ImportState.READY_TO_APPROVE:
      return { isDisabled: false, label: 'Approve' };
    case ImportState.ASKING_USER_TO_APPROVE:
      return { isDisabled: true, label: 'Check Wallet' };
    case ImportState.APPROVING:
      return { isDisabled: true, label: 'Approving...' };
    case ImportState.READY_TO_MINT:
      return { isDisabled: false, label: 'Mint' };
    case ImportState.ASKING_USER_TO_MINT:
      return { isDisabled: true, label: 'Check Wallet' };
    case ImportState.UNABLE_TO_MINT:
      return { isDisabled: true, label: 'Unable to Mint' };
    case ImportState.LOADING:
    default:
      return { isDisabled: true, label: 'Loading...' };
  }
}

export type ImportBoostWidgetProps = {
  cardInfo: BoostCardInfo;
  boostFactor: number;
  iv: number;
  setBoostFactor: (boostFactor: number) => void;
  setPendingTxn: (txn: WriteContractReturnType | null) => void;
};

export default function ImportBoostWidget(props: ImportBoostWidgetProps) {
  const { cardInfo, boostFactor, iv, setBoostFactor, setPendingTxn } = props;
  const activeChain = useChain();
  const [twentyFourHourPoolData, setTwentyFourHourPoolData] = useState<TwentyFourHourPoolData | undefined>(undefined);
  const [tokenQuotes, setTokenQuotes] = useState<TokenQuote[] | undefined>(undefined);
  const [maxSlippagePercentage, setSlippagePercentage] = useState('0.10');

  const { address: userAddress } = useAccount();

  const { lendingPairs } = useLendingPairs(activeChain.id);
  const lendingPair = useLendingPair(lendingPairs, cardInfo.uniswapPool);

  // Generate labels for input range (slider)
  const labels: string[] = [];
  for (let i = BOOST_MIN; i <= BOOST_MAX; i += 1) {
    if (i % 2 !== 0) {
      labels.push(`${i.toFixed(0)}x`);
    } else {
      labels.push('');
    }
  }

  useEffect(() => {
    (async () => {
      let quoteDataResponse: AxiosResponse<PriceRelayLatestResponse>;
      try {
        quoteDataResponse = await axios.get(
          `${API_PRICE_RELAY_LATEST_URL}?symbols=${cardInfo.token0.symbol},${cardInfo.token1.symbol}`
        );
      } catch {
        return;
      }
      const prResponse: PriceRelayLatestResponse = quoteDataResponse.data;
      if (!prResponse) {
        return;
      }
      const tokenQuoteData: TokenQuote[] = Object.entries(prResponse).map(([key, value]) => {
        return {
          token: getTokenBySymbol(activeChain.id, key)!,
          price: value.price,
        };
      });
      setTokenQuotes(tokenQuoteData);
    })();
  }, [activeChain.id, cardInfo.token0.symbol, cardInfo.token1.symbol, setTokenQuotes]);

  const borrowAmount0 = GN.fromDecimalString(
    (cardInfo.amount0() * (boostFactor - 1)).toFixed(cardInfo.token0.decimals),
    cardInfo.token0.decimals
  );
  const borrowAmount1 = GN.fromDecimalString(
    (cardInfo.amount1() * (boostFactor - 1)).toFixed(cardInfo.token1.decimals),
    cardInfo.token1.decimals
  );

  const { apr0, apr1 } = useMemo(() => {
    if (!lendingPair) return { apr0: null, apr1: null };

    return {
      apr0: lendingPair.kitty0Info.hypotheticalBorrowAPR(borrowAmount0) * 100,
      apr1: lendingPair.kitty1Info.hypotheticalBorrowAPR(borrowAmount1) * 100,
    };
  }, [lendingPair, borrowAmount0, borrowAmount1]);

  useEffect(() => {
    (async () => {
      const theGraphClient = getTheGraphClient(activeChain.id);
      const unixTwoDaysAgo = Math.floor(Date.now() / 1000) - 86400 * 2;
      const initialQueryResponse = (await theGraphClient.query({
        query: Uniswap24HourPoolDataQuery,
        variables: {
          poolAddress: cardInfo.uniswapPool.toLowerCase(),
          date: unixTwoDaysAgo,
        },
        errorPolicy: 'ignore',
      })) as ApolloQueryResult<UniswapV3GraphQL24HourPoolDataQueryResponse>;
      if (initialQueryResponse.data.poolDayDatas) {
        const poolDayData = initialQueryResponse.data.poolDayDatas[0];
        setTwentyFourHourPoolData({
          liquidity: new Big(poolDayData.liquidity),
          feesUSD: parseInt(poolDayData.feesUSD),
        });
      }
    })();
  }, [activeChain.id, cardInfo, setTwentyFourHourPoolData]);

  const dailyInterestUSD = useMemo(() => {
    if (apr0 == null || apr1 == null || !tokenQuotes) {
      return null;
    }
    const dailyInterest0 = (apr0 / 365) * (cardInfo.amount0() * (boostFactor - 1));
    const dailyInterest1 = (apr1 / 365) * (cardInfo.amount1() * (boostFactor - 1));
    const dailyInterestUSD0 = dailyInterest0 * tokenQuotes[0].price;
    const dailyInterestUSD1 = dailyInterest1 * tokenQuotes[1].price;
    return dailyInterestUSD0 + dailyInterestUSD1;
  }, [apr0, apr1, boostFactor, cardInfo, tokenQuotes]);

  const nftTokenId = BigInt(cardInfo.nftTokenId);
  const modifyData = useMemo(() => {
    if (!cardInfo) return undefined;
    const { position } = cardInfo;
    const maxSlippage = parseFloat(maxSlippagePercentage) / 100;
    const maxBorrowAmount0 = borrowAmount0
      .recklessMul(1 + maxSlippage)
      .toBigNumber()
      .add(2);
    const maxBorrowAmount1 = borrowAmount1
      .recklessMul(1 + maxSlippage)
      .toBigNumber()
      .add(2);
    // 112 bits for maxBorrowAmount0, 112 bits for maxBorrowAmount1
    const combinedMaxBorrowAmount = maxBorrowAmount0.add(maxBorrowAmount1.shl(112));
    const inner = ethers.utils.defaultAbiCoder.encode(
      ['uint256', 'int24', 'int24', 'uint128', 'uint24', 'uint224'],
      [
        cardInfo.nftTokenId,
        position.lower,
        position.upper,
        position.liquidity.toString(10),
        (boostFactor * 10000).toFixed(0),
        combinedMaxBorrowAmount,
      ]
    ) as Hex;
    const actionId = 0;
    return ethers.utils.defaultAbiCoder.encode(['uint8', 'bytes'], [actionId, inner]) as Hex;
  }, [cardInfo, maxSlippagePercentage, borrowAmount0, borrowAmount1, boostFactor]);
  const enableHooks = cardInfo !== undefined;

  // Read who the manager is supposed to be
  const necessaryManager = STACKING_SALMON_BOOST_MANAGER_ADDRESS[activeChain.id];

  // Read who is approved to manage this Kodiak NFT
  const {
    data: manager,
    refetch: refetchManager,
    isFetching: isFetchingManager,
  } = useReadContract({
    address: UNISWAP_NONFUNGIBLE_POSITION_MANAGER_ADDRESS[activeChain.id],
    abi: erc721Abi,
    functionName: 'getApproved',
    args: [nftTokenId],
    chainId: activeChain.id,
    query: { enabled: enableHooks },
  });
  const managerIsCorrect = Boolean(manager) && manager === necessaryManager;
  const shouldWriteManager = !isFetchingManager && Boolean(manager) && !managerIsCorrect;
  const shouldMint = !isFetchingManager && Boolean(modifyData) && managerIsCorrect;

  // We need the Boost Manager to be approved, so if it's not, prepare to write
  const { data: configWriteManager } = useSimulateContract({
    address: UNISWAP_NONFUNGIBLE_POSITION_MANAGER_ADDRESS[activeChain.id],
    abi: erc721Abi,
    functionName: 'approve',
    args: [necessaryManager ?? '0x', nftTokenId],
    chainId: activeChain.id,
    query: { enabled: enableHooks && shouldWriteManager },
  });
  const {
    writeContract: writeManager,
    data: writeManagerTxn,
    isPending: isAskingUserToWriteManager,
  } = useWriteContract();

  // Wait for the approval transaction to go through, then refetch manager
  const { data: managerReceipt, isLoading: isWritingManager } = useWaitForTransactionReceipt({
    confirmations: 1,
    hash: writeManagerTxn,
    chainId: activeChain.id,
  });

  useEffect(() => {
    if (managerReceipt) refetchManager();
  }, [managerReceipt, refetchManager]);

  // The NFT index we will use if minting
  const { data: nextNftPtrIdx } = useReadContract({
    address: STACKING_SALMON_BORROWER_NFT_ADDRESS[activeChain.id],
    abi: borrowerNftAbi,
    functionName: 'balanceOf',
    args: [userAddress ?? '0x'],
    chainId: activeChain.id,
    query: { enabled: enableHooks && Boolean(userAddress) },
  });

  const borrowerNftFilterParams = useMemo(
    () => ({
      validUniswapPool: cardInfo.uniswapPool,
      onlyCheckMostRecentModify: true,
      includeUnusedBorrowers: true,
    }),
    [cardInfo.uniswapPool]
  );
  const { borrowerNftRefs } = useBorrowerNftRefs(userAddress, activeChain.id, borrowerNftFilterParams);
  const availableNft = useMemo(() => {
    return borrowerNftRefs.length > 0
      ? {
          borrower: borrowerNftRefs[0].address,
          ptrIdx: borrowerNftRefs[0].index,
        }
      : undefined;
  }, [borrowerNftRefs]);

  // If we're reusing an old NFT, check whether it has ANTE
  const { data: borrowerBalance } = useBalance({
    address: availableNft?.borrower ?? '0x',
    chainId: activeChain.id,
    query: { enabled: availableNft !== undefined },
  });

  const ethToSend = useMemo(() => {
    if (!lendingPair) return 0n;
    return lendingPair.amountEthRequiredBeforeBorrowing(borrowerBalance?.value ?? 0n);
  }, [lendingPair, borrowerBalance]);

  // First, we `mint` so that they have a `Borrower` to put stuff in
  const encodedMint = useMemo(() => {
    if (!userAddress) return '0x';
    const to = userAddress;
    const pools = [cardInfo.uniswapPool];
    const salts = [generateBytes12Salt()];
    return encodeFunctionData({
      abi: borrowerNftAbi,
      functionName: 'mint',
      args: [to, pools, salts],
    });
  }, [userAddress, cardInfo]);
  // Then we `modify`, calling the BoostManager to import the Kodiak position
  const encodedModify = useMemo(() => {
    if (!userAddress || nextNftPtrIdx === undefined || modifyData === undefined) return '0x';
    const owner = userAddress;
    const indices = [availableNft !== undefined ? availableNft.ptrIdx : Number(nextNftPtrIdx)];
    const managers = [STACKING_SALMON_BOOST_MANAGER_ADDRESS[activeChain.id]];
    const datas = [modifyData];
    const antes = [Number(ethToSend / 10_000_000_000_000n)];
    return encodeFunctionData({
      abi: borrowerNftAbi,
      functionName: 'modify',
      args: [owner, indices, managers, datas, antes],
    });
  }, [userAddress, activeChain, availableNft, nextNftPtrIdx, modifyData, ethToSend]);

  const {
    data: configMint,
    isError: isUnableToMint,
    isLoading: isCheckingIfAbleToMint,
  } = useSimulateContract({
    address: STACKING_SALMON_BORROWER_NFT_ADDRESS[activeChain.id],
    abi: borrowerNftAbi,
    functionName: 'multicall',
    args: [availableNft !== undefined ? [encodedModify] : [encodedMint, encodedModify]],
    value: ethToSend,
    chainId: activeChain.id,
    query: { enabled: userAddress && enableHooks && shouldMint && Boolean(encodedMint) && Boolean(encodedModify) },
  });
  const { writeContractAsync: mint, isPending: isAskingUserToMint } = useWriteContract();

  const dailyFeesEarned = useMemo(() => {
    if (!twentyFourHourPoolData || !cardInfo) return null;
    if (!cardInfo.isInRange()) return 0;

    const { liquidity } = cardInfo.position;
    const userLiquidity = new Big(liquidity.toString());
    const { liquidity: totalLiquidity, feesUSD } = twentyFourHourPoolData;
    return userLiquidity.div(totalLiquidity).toNumber() * feesUSD * boostFactor;
  }, [twentyFourHourPoolData, cardInfo, boostFactor]);

  const dailyILSuffered = useMemo(() => {
    if (!cardInfo || !tokenQuotes) return null;

    const log10001E = Math.log(Math.E) / Math.log(1.0001);
    const tick24hLow = Math.max(cardInfo.currentTick - 3 * iv * log10001E, TickMath.MIN_TICK + 1);
    // const tick24hHigh = Math.min(cardInfo.currentTick + 3 * iv * log10001E, TickMath.MAX_TICK - 1);

    const position: UniswapPosition = {
      lower: cardInfo.position.lower,
      upper: cardInfo.position.upper,
      liquidity: JSBI.divide(
        JSBI.multiply(cardInfo.position.liquidity, JSBI.BigInt((boostFactor * 10000).toFixed(0))),
        JSBI.BigInt(10000)
      ),
    };

    const valueCurrent = getValueOfLiquidity(position, cardInfo.currentTick, cardInfo.token1.decimals);
    const value24hLow = getValueOfLiquidity(position, Math.round(tick24hLow), cardInfo.token1.decimals);
    // const value24hHigh = getValueOfLiquidity(position, Math.round(tick24hHigh), cardInfo.token1.decimals);

    const lossEstimate = valueCurrent - value24hLow;
    return lossEstimate * tokenQuotes[1].price;
  }, [cardInfo, tokenQuotes, boostFactor, iv]);

  let state: ImportState = ImportState.LOADING;
  if (isWritingManager) {
    state = ImportState.APPROVING;
  } else if (isAskingUserToWriteManager) {
    state = ImportState.ASKING_USER_TO_APPROVE;
  } else if (isAskingUserToMint) {
    state = ImportState.ASKING_USER_TO_MINT;
  } else if (shouldWriteManager && configWriteManager) {
    state = ImportState.READY_TO_APPROVE;
  } else if (isUnableToMint) {
    state = ImportState.UNABLE_TO_MINT;
  } else if (isCheckingIfAbleToMint) {
    state = ImportState.LOADING;
  } else if (shouldMint && configMint) {
    state = ImportState.READY_TO_MINT;
  }

  const buttonState = getImportButtonState(state);

  return (
    <Container>
      <div className='w-full grid grid-flow-col grid-cols-3'>
        <div />
        <Text size='L'>Boost Factor</Text>
        <div className='ml-auto'>
          <SlippageWidget updateSlippagePercentage={setSlippagePercentage} />
        </div>
      </div>
      <SliderContainer>
        <Display size='M'>{`${boostFactor}x`}</Display>
        <LeverageSlider
          type='range'
          list='boost-factor-labels'
          min={BOOST_MIN}
          max={BOOST_MAX}
          step={0.1}
          value={boostFactor}
          onChange={(e) => setBoostFactor(Number(e.target.value))}
        />
        <StyledDatalist id='boost-factor-labels'>
          {labels.map((label, i) => (
            <option key={i} value={i + 1} label={label}></option>
          ))}
        </StyledDatalist>
      </SliderContainer>
      <Text size='M' color={SECONDARY_COLOR} className='mt-4'>
        Estimated Earnings
      </Text>
      <div className='flex justify-center gap-2 mt-2'>
        <div className='flex flex-row justify-center items-end'>
          <Display size='S' color={SECONDARY_COLOR}>
            {formatUSD(dailyFeesEarned)}
          </Display>
          <Text size='S' color={SECONDARY_COLOR} className='ml-1'>
            / day
          </Text>
        </div>
      </div>
      <div className='flex flex-row justify-center gap-12'>
        <div className='flex flex-col justify-center gap-2 mt-6'>
          <Text size='M' color={SECONDARY_COLOR}>
            3σ IL
          </Text>
          <div className='flex flex-row justify-center items-end'>
            <Display size='S' color={SECONDARY_COLOR}>
              -{formatUSD(dailyILSuffered)}
            </Display>
            <Text size='S' color={SECONDARY_COLOR} className='ml-1'>
              / day
            </Text>
          </div>
        </div>
        <div className='flex flex-col justify-center gap-2 mt-6'>
          <Text size='M' color={SECONDARY_COLOR}>
            Estimated Interest
          </Text>
          <div className='flex flex-row justify-center items-end'>
            <Display size='S' color={SECONDARY_COLOR}>
              -{formatUSD(dailyInterestUSD)}
            </Display>
            <Text size='S' color={SECONDARY_COLOR} className='ml-1'>
              / day
            </Text>
          </div>
        </div>
      </div>
      <div className='mt-6 mx-6'>
        <div className='flex flex-col gap-1 w-full text-start mb-4'>
          <Text size='M' weight='bold'>
            Summary
          </Text>
          <Text size='XS' color={SECONDARY_COLOR} className='w-full text-start overflow-hidden text-ellipsis'>
            You're moving liquidity from a Kodiak NFT to a Sammin NFT and applying a{' '}
            <strong>{boostFactor.toFixed(1)}x boost</strong>. As a result, you will earn swap fees{' '}
            {boostFactor.toFixed(1)}x faster, but also pay interest to Stacking Salmon lenders and risk liquidation.
            Liquidation thresholds are indicated with (!) in the graph to the left. You have selected a slippage
            tolerance of <strong>{maxSlippagePercentage}%</strong>.
          </Text>
          <Text size='XS' color={TERTIARY_COLOR} className='overflow-hidden text-ellipsis'>
            You will need to provide an additional {GN.fromBigInt(ethToSend, 18).toString(GNFormat.LOSSY_HUMAN)} BERA to
            cover gas fees in the event that you get liquidated. If you don't get liquidated, the BERA will be returned
            to you when you close the position.
          </Text>
        </div>
        <FilledGradientButton
          size='M'
          onClick={() => {
            if (state === ImportState.READY_TO_APPROVE) {
              writeManager(configWriteManager!.request);
            } else if (state === ImportState.READY_TO_MINT) {
              mint(configMint!.request)
                .then((hash) => setPendingTxn(hash))
                .catch((e) => console.error(e));
            }
          }}
          disabled={buttonState.isDisabled}
          fillWidth={true}
        >
          {buttonState.label}
        </FilledGradientButton>
        <Text size='XS' color={TERTIARY_COLOR} className='w-full text-start mt-2'>
          By using this interface, you agree to our{' '}
          <a href={TERMS_OF_SERVICE_URL} className='underline' rel='noreferrer' target='_blank'>
            Terms of Service
          </a>{' '}
          and acknowledge that you may lose your money. Stacking Salmon is not responsible for any losses you may incur.
          It is your duty to educate yourself and be aware of the risks.
        </Text>
      </div>
    </Container>
  );
}
